// Today's meals widget. Each of the 4 slots (Breakfast / Lunch / Snack /
// Dinner) renders one tile that shows the latest log for that slot when
// present (item chips + photo thumbnail + total kcal) or a "Tap to log"
// affordance otherwise. Tapping the tile opens the rich FoodLogDialog
// (search + multi-add + photo + notes).

import React, { useState } from 'react'
import styled from 'styled-components'
import WidgetCard from './WidgetCard'
import FoodLogDialog from '../../care/components/FoodLogDialog'
import { usePatient } from '../../../context/PatientContext'
import * as dietService from '../../../services/dietService'

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  @media (min-width: 540px) {
    grid-template-columns: repeat(4, 1fr);
  }
`

const Slot = styled.button`
  appearance: none;
  border: 1px solid
    ${(p) =>
      p.$logged ? p.theme?.color?.accent || '#6B9080' : p.theme?.color?.border || '#E6E0D6'};
  background: ${(p) =>
    p.$logged ? p.theme?.color?.accentSoft || '#E1ECE7' : p.theme?.color?.bgRaised || '#fff'};
  border-radius: 14px;
  padding: 12px;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  transition: all 200ms ease-out;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 96px;
  &:hover {
    border-color: ${(p) => p.theme?.color?.primary || '#1F3A5F'};
  }
`

const SlotHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

const SlotLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
`

const KcalBadge = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: ${(p) => p.theme?.color?.accent || '#3F7D58'};
  background: rgba(255, 255, 255, 0.6);
  padding: 1px 7px;
  border-radius: 999px;
`

const SlotBody = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  flex: 1;
`

const Thumb = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  overflow: hidden;
  flex-shrink: 0;
  background: ${(p) => p.theme?.color?.bgSubtle || '#F4F0E9'};
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
`

const ThumbImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const ItemList = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const ItemLine = styled.div`
  font-size: 13px;
  color: ${(p) => p.theme?.color?.ink || '#1F2937'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 600;
`

const ItemSub = styled.div`
  font-size: 12px;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const TapHint = styled.div`
  font-size: 13px;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
  font-style: italic;
`

const SlotEmptyHint = styled.div`
  font-size: 11px;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
`

function summariseEntries(slot) {
  // Prefer the rich entries shape; fall back to the legacy summary string.
  if (Array.isArray(slot.entries) && slot.entries.length > 0) {
    return slot.entries
  }
  if (slot.summary) {
    return slot.summary
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => ({ displayName: s }))
  }
  return null
}

export default function MealsTodayCard({ data, onLogged }) {
  const { currentProfile } = usePatient()
  const [activeSlot, setActiveSlot] = useState(null)

  if (!data || data.length === 0) return null

  const loggedCount = data.filter((s) => s.logged).length
  const totalKcalToday = data.reduce(
    (sum, s) => sum + (typeof s.totalCalories === 'number' ? s.totalCalories : 0),
    0,
  )

  function openLog(slot) {
    setActiveSlot({ slot: slot.slot, slotLabel: slot.slotLabel })
  }

  async function handleSubmit(body) {
    await dietService.log(currentProfile, body)
    if (onLogged) onLogged()
  }

  return (
    <>
      <WidgetCard
        title="Meals today"
        badge={
          totalKcalToday > 0
            ? `${loggedCount}/${data.length} · ~${totalKcalToday} kcal`
            : `${loggedCount}/${data.length} logged`
        }
      >
        <Grid>
          {data.map((s) => {
            const entries = summariseEntries(s)
            const firstFew = entries ? entries.slice(0, 2) : null
            const overflow = entries && entries.length > 2 ? entries.length - 2 : 0
            return (
              <Slot
                key={s.slot}
                $logged={s.logged}
                onClick={() => openLog(s)}
                aria-label={`Log ${s.slotLabel}`}
              >
                <SlotHeader>
                  <SlotLabel>{s.slotLabel}</SlotLabel>
                  {s.totalCalories ? <KcalBadge>{s.totalCalories} kcal</KcalBadge> : null}
                </SlotHeader>
                <SlotBody>
                  {s.photoUrl ? (
                    <Thumb>
                      <ThumbImg src={s.photoUrl} alt="" />
                    </Thumb>
                  ) : null}
                  <ItemList>
                    {s.logged && firstFew ? (
                      <>
                        <ItemLine>
                          {firstFew.map((e) => e.displayName).join(', ')}
                          {overflow > 0 ? `, +${overflow} more` : ''}
                        </ItemLine>
                        {firstFew[0]?.quantity ? (
                          <ItemSub>
                            {firstFew[0].quantity}
                            {overflow > 0 ? '' : ''}
                          </ItemSub>
                        ) : null}
                      </>
                    ) : s.logged ? (
                      <ItemLine>Logged</ItemLine>
                    ) : (
                      <TapHint>Tap to log</TapHint>
                    )}
                    {!s.logged && Array.isArray(s.recommendedItems) && s.recommendedItems.length > 0 ? (
                      <SlotEmptyHint>
                        Plan: {s.recommendedItems.slice(0, 1).join(', ')}
                      </SlotEmptyHint>
                    ) : null}
                  </ItemList>
                </SlotBody>
              </Slot>
            )
          })}
        </Grid>
      </WidgetCard>

      <FoodLogDialog
        open={!!activeSlot}
        mealType={activeSlot?.slot}
        mealLabel={activeSlot?.slotLabel}
        onClose={() => setActiveSlot(null)}
        onSubmit={handleSubmit}
      />
    </>
  )
}
